'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Logo from './Logo';
import { siteConfig } from '../../config/site';
import {
  Bars3Icon,
  XMarkIcon,
  PhoneIcon,
  ChevronDownIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  TvIcon,
  BoltIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';

const serviceCategories = [
  {
    icon: DevicePhoneMobileIcon,
    label: 'Phones & Tablets',
    href: '/services/phone-repair',
    items: [
      { label: 'iPhone Repair', href: '/services/iphone-repair' },
      { label: 'Samsung Phone Repair', href: '/services/samsung-phone-repair' },
      { label: 'iPad Repair', href: '/services/ipad-repair' },
      { label: 'Screen Replacement', href: '/services/phone-screen-replacement' },
      { label: 'Battery Replacement', href: '/services/phone-battery-replacement' },
    ],
  },
  {
    icon: ComputerDesktopIcon,
    label: 'Laptops & Computers',
    href: '/services/laptop-computer-repair',
    items: [
      { label: 'Laptop Repair', href: '/services/laptop-repair' },
      { label: 'MacBook Repair', href: '/services/macbook-repair' },
      { label: 'Laptop Screen Repair', href: '/services/laptop-screen-repair' },
      { label: 'Computer Repair', href: '/services/computer-repair' },
    ],
  },
  {
    icon: TvIcon,
    label: 'Gaming Consoles',
    href: '/services/game-console-repair',
    items: [
      { label: 'PS5 Repair', href: '/services/ps5-repair' },
      { label: 'PS5 Cleaning', href: '/services/ps5-cleaning' },
      { label: 'PS5 HDMI Port Repair', href: '/services/ps5-hdmi-port-repair' },
      { label: 'Xbox Repair', href: '/services/xbox-repair' },
      { label: 'Nintendo Switch Repair', href: '/services/nintendo-switch-repair' },
    ],
  },
  {
    icon: BoltIcon,
    label: 'Electronics',
    href: '/services/electronics-repair',
    items: [
      { label: 'Electronics Repair', href: '/services/electronics-repair' },
    ],
  },
  {
    icon: BuildingOfficeIcon,
    label: 'Business & Office',
    href: '/services/business-office-repair',
    items: [
      { label: 'Business & Office Repair', href: '/services/business-office-repair' },
    ],
  },
];

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Services', href: '/services', hasDropdown: true },
  { label: 'Blog', href: '/blog' },
  { label: 'On-Site Service', href: '/on-site-service' },
  { label: 'Reviews', href: '/reviews' },
  { label: 'Contact', href: '/contact' },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      <header
        className="sticky top-0 z-40 transition-all duration-200"
        style={{
          backgroundColor: scrolled ? 'rgba(11, 13, 15, 0.95)' : 'var(--bg)',
          borderBottom: '1px solid var(--border-color)',
          backdropFilter: scrolled ? 'blur(12px)' : 'none',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <Logo size="md" />

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation">
              {navLinks?.map((link) =>
                link?.hasDropdown ? (
                  <div
                    key={link?.label}
                    className="relative"
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    <button
                      className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                      style={{ color: servicesOpen ? 'var(--brand)' : 'var(--text-muted)' }}
                      aria-expanded={servicesOpen}
                    >
                      {link?.label}
                      <ChevronDownIcon className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {servicesOpen && (
                      <div
                        className="absolute top-full left-1/2 -translate-x-1/2 w-[640px] rounded-xl p-4 mt-1 grid grid-cols-2 gap-3"
                        style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border-color)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
                      >
                        {serviceCategories?.map((cat) => (
                          <div key={cat?.label} className="space-y-1">
                            <Link
                              href={cat?.href}
                              className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors hover:opacity-80"
                              style={{ color: 'var(--brand)' }}
                            >
                              <cat.icon className="w-4 h-4 flex-shrink-0" />
                              {cat?.label}
                            </Link>
                            {cat?.items?.map((item) => (
                              <Link
                                key={item?.href}
                                href={item?.href}
                                className="block px-2 py-1 rounded text-sm transition-colors hover:opacity-80"
                                style={{ color: 'var(--text-muted)' }}
                              >
                                {item?.label}
                              </Link>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link?.label}
                    href={link?.href}
                    className="px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                    style={{ color: pathname === link?.href ? 'var(--text)' : 'var(--text-muted)' }}
                  >
                    {link?.label}
                  </Link>
                )
              )}
            </nav>

            {/* Desktop CTAs */}
            <div className="hidden lg:flex items-center gap-2">
              <a
                href={siteConfig?.phone?.tel}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all hover:opacity-90"
                style={{ color: 'var(--text)', borderColor: 'var(--border-color)' }}
              >
                <PhoneIcon className="w-4 h-4" />
                Call Now
              </a>
              <Link
                href="/request-service"
                className="px-4 py-2 rounded-lg text-sm font-bold transition-all hover:opacity-90 active:scale-95"
                style={{ backgroundColor: 'var(--brand)', color: 'var(--bg)' }}
              >
                Request Service
              </Link>
            </div>

            {/* Mobile hamburger */}
            <button
              className="lg:hidden p-2 rounded-lg"
              style={{ color: 'var(--text)' }}
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              aria-expanded={mobileOpen}
            >
              <Bars3Icon className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-50 flex flex-col"
          style={{ backgroundColor: 'var(--bg)' }}
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation menu"
        >
          <div className="flex items-center justify-between px-4 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
            <Logo size="sm" />
            <button
              onClick={() => setMobileOpen(false)}
              className="p-2 rounded-lg"
              style={{ color: 'var(--text)' }}
              aria-label="Close menu"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-6 space-y-3">
            {/* Top action buttons */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <a
                href={siteConfig?.phone?.tel}
                className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold border transition-all"
                style={{ borderColor: 'var(--border-color)', color: 'var(--text)' }}
              >
                <PhoneIcon className="w-4 h-4" />
                Call Now
              </a>
              <a
                href={siteConfig?.phone?.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold border transition-all"
                style={{ borderColor: 'var(--border-color)', color: 'var(--text)' }}
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                WhatsApp
              </a>
            </div>
            <Link
              href="/request-service"
              className="flex items-center justify-center py-3.5 rounded-xl text-sm font-bold w-full mb-4 transition-all"
              style={{ backgroundColor: 'var(--brand)', color: 'var(--bg)' }}
            >
              Request Service
            </Link>

            {/* Nav links */}
            {navLinks?.map((link) =>
              link?.hasDropdown ? (
                <div key={link?.label}>
                  <button
                    className="w-full flex items-center justify-between py-3 text-base font-semibold"
                    style={{ color: 'var(--text)' }}
                    onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  >
                    {link?.label}
                    <ChevronDownIcon className={`w-5 h-5 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} style={{ color: 'var(--text-muted)' }} />
                  </button>
                  {mobileServicesOpen && (
                    <div className="pl-4 space-y-1 pb-2">
                      {serviceCategories?.map((cat) => (
                        <div key={cat?.label}>
                          <Link
                            href={cat?.href}
                            className="block py-2 text-sm font-semibold"
                            style={{ color: 'var(--brand)' }}
                          >
                            {cat?.label}
                          </Link>
                          {cat?.items?.map((item) => (
                            <Link
                              key={item?.href}
                              href={item?.href}
                              className="block py-1.5 text-sm pl-3"
                              style={{ color: 'var(--text-muted)' }}
                            >
                              {item?.label}
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link?.label}
                  href={link?.href}
                  className="block py-3 text-base font-semibold border-b"
                  style={{ color: 'var(--text)', borderColor: 'var(--border-color)' }}
                >
                  {link?.label}
                </Link>
              )
            )}
          </div>
        </div>
      )}
    </>
  );
}